/**
 * AdminBugReports.tsx — ekran „Zgłoszenia" w panelu ITE (scope admin).
 *
 * Lista zgłoszeń błędów od użytkowników tenantów (dane z `/api/admin/bug-reports`).
 * Dwa widoki: TABELA (sort + filtr per kolumna, client-side — `ConfigurableTable`
 * `persistConfig={false}`) oraz GALERIA (kafelki). Klik w wiersz/kafelek otwiera MODAL
 * ze szczegółami (metadane + edycja priorytetu / statusu / osoby przydzielonej +
 * galeria zrzutów z powiększaniem). Triage zapisywany przez PATCH; przydzielenie do
 * realnych operatorów ITE (`/api/admin/admins`).
 *
 * Kolumna „Opiekun tenanta" jest zaślepką („—") — koncept opiekuna nie istnieje jeszcze
 * po stronie tenantów (do dołożenia osobno).
 */

import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Bug, ChevronLeft, ChevronRight, LayoutGrid, List, Paperclip } from 'lucide-react';

import { Badge } from '@/components/Badge';
import { ConfigurableTable } from '@/components/configurable-table';
import type { ColumnDefinition } from '@/components/configurable-table/types';
import { IconButton } from '@/components/IconButton';
import { Layout } from '@/components/Layout';
import { Modal } from '@/components/Modal';
import { SearchableSelect } from '@/components/SearchableSelect';
import { Skeleton } from '@/components/Skeleton';
import { PreviewTenantAuthBoundary } from '@/contexts/TenantAuthContext';
import { useToast } from '@/contexts/ToastContext';
import { api } from '@/lib/api';
import { cn } from '@/lib/utils';

type BugPriority = 'low' | 'medium' | 'high' | 'critical';
type BugStatus = 'new' | 'in_progress' | 'resolved' | 'rejected';
type ViewMode = 'table' | 'gallery';

interface BugReportRow {
  id: string;
  createdAt: string;
  reporterName: string | null;
  reporterEmail: string;
  tenantName: string | null;
  tenantCode: string | null;
  appVersion: string | null;
  pageUrl: string | null;
  message: string;
  priority: BugPriority;
  status: BugStatus;
  assigneeAdminId: string | null;
  assigneeName: string | null;
  assigneeEmail: string | null;
  screenshots: number;
  caretaker: string;
}

interface BugAttachmentDto {
  id: string;
  fileName: string;
  mimeType: string;
  sizeBytes: number;
}

const PRIORITIES: BugPriority[] = ['low', 'medium', 'high', 'critical'];
const STATUSES: BugStatus[] = ['new', 'in_progress', 'resolved', 'rejected'];
const UNASSIGNED = '';

const PRIORITY_DOT: Record<BugPriority, string> = {
  low: 'bg-text-muted',
  medium: 'bg-amber-500',
  high: 'bg-orange-500',
  critical: 'bg-red-500',
};

const PRIORITY_TONE: Record<BugPriority, 'neutral' | 'info' | 'warning' | 'danger'> = {
  low: 'neutral',
  medium: 'info',
  high: 'warning',
  critical: 'danger',
};

const STATUS_TONE: Record<BugStatus, 'neutral' | 'info' | 'warning' | 'success'> = {
  new: 'info',
  in_progress: 'warning',
  resolved: 'success',
  rejected: 'neutral',
};

function formatDateTime(iso: string): string {
  try {
    return new Date(iso).toLocaleString('pl-PL', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return iso;
  }
}

function contentUrl(reportId: string, attachmentId: string): string {
  return `/api/admin/bug-reports/${reportId}/attachments/${attachmentId}/content`;
}

export function AdminBugReports() {
  const { t } = useTranslation();
  const toast = useToast();
  const queryClient = useQueryClient();

  const [view, setView] = useState<ViewMode>('table');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);

  const reportsQuery = useQuery({
    queryKey: ['admin', 'bug-reports'],
    queryFn: async () => {
      const res = await api.get<{ reports: BugReportRow[] }>('/admin/bug-reports');
      return res.data.reports;
    },
    // Zgłoszenia wpadają od tenantów w tle — bez tego nowe było widać dopiero po
    // odświeżeniu strony (zgłoszenie ze szkolenia 14.10). Co 30 s, tylko gdy karta widoczna.
    refetchInterval: 30_000,
  });
  const rows = useMemo(() => reportsQuery.data ?? [], [reportsQuery.data]);

  // Operatorzy ITE do przydzielania.
  const adminsQuery = useQuery({
    queryKey: ['admin', 'admins', 'for-assign'],
    queryFn: async () => {
      const res = await api.get<{
        admins: Array<{ id: string; name: string | null; email: string }>;
      }>('/admin/admins');
      return res.data.admins;
    },
  });

  // Szczegóły (załączniki) wybranego zgłoszenia.
  const detailQuery = useQuery({
    queryKey: ['admin', 'bug-report', selectedId],
    queryFn: async () => {
      const res = await api.get<{ attachments: BugAttachmentDto[] }>(
        `/admin/bug-reports/${selectedId}`,
      );
      return res.data.attachments;
    },
    enabled: selectedId !== null,
  });

  const triageMutation = useMutation({
    mutationFn: async (args: {
      id: string;
      patch: Partial<Pick<BugReportRow, 'priority' | 'status'>> & {
        assigneeAdminId?: string | null;
      };
    }) => {
      await api.patch(`/admin/bug-reports/${args.id}`, args.patch);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['admin', 'bug-reports'] });
    },
    onError: () => {
      toast.show({ variant: 'error', title: t('admin.bugReports.saveError') });
    },
  });

  const selected = rows.find((r) => r.id === selectedId) ?? null;

  const priorityOptions = useMemo(
    () => PRIORITIES.map((p) => ({ value: p, label: t(`admin.bugReports.priority.${p}`) })),
    [t],
  );
  const statusOptions = useMemo(
    () => STATUSES.map((s) => ({ value: s, label: t(`admin.bugReports.status.${s}`) })),
    [t],
  );
  const assigneeOptions = useMemo(
    () => [
      { value: UNASSIGNED, label: t('admin.bugReports.unassigned') },
      ...(adminsQuery.data ?? []).map((a) => ({ value: a.id, label: a.name || a.email })),
    ],
    [adminsQuery.data, t],
  );

  const tenantOptions = useMemo(
    () =>
      Array.from(new Set(rows.map((r) => r.tenantName).filter((v): v is string => !!v))).map(
        (v) => ({
          value: v,
          label: v,
        }),
      ),
    [rows],
  );
  const versionOptions = useMemo(
    () =>
      Array.from(new Set(rows.map((r) => r.appVersion).filter((v): v is string => !!v))).map(
        (v) => ({
          value: v,
          label: v,
        }),
      ),
    [rows],
  );
  const caretakerOptions = useMemo(
    () =>
      Array.from(new Set(rows.map((r) => r.caretaker).filter((v) => v.length > 0))).map((v) => ({
        value: v,
        label: v,
      })),
    [rows],
  );

  const kpis = useMemo(
    () => ({
      total: rows.length,
      new: rows.filter((r) => r.status === 'new').length,
      inProgress: rows.filter((r) => r.status === 'in_progress').length,
      highPriority: rows.filter((r) => r.priority === 'high' || r.priority === 'critical').length,
    }),
    [rows],
  );

  const priorityLabel = (p: BugPriority) => t(`admin.bugReports.priority.${p}`);
  const statusLabel = (s: BugStatus) => t(`admin.bugReports.status.${s}`);
  const stopRowOpen = (e: React.MouseEvent) => e.stopPropagation();

  const columns = useMemo<ColumnDefinition<BugReportRow>[]>(
    () => [
      {
        id: 'createdAt',
        accessor: (row) => (row.createdAt ? row.createdAt.slice(0, 10) : ''),
        header: t('admin.bugReports.columns.createdAt'),
        sortable: true,
        headerFilter: { category: 'date' },
        minWidth: 150,
        renderCell: (_v, row) => (
          <span className="whitespace-nowrap font-mono text-xs text-text-secondary">
            {formatDateTime(row.createdAt)}
          </span>
        ),
      },
      {
        id: 'reporter',
        accessor: (row) => `${row.reporterName ?? ''} ${row.reporterEmail}`,
        header: t('admin.bugReports.columns.reporter'),
        sortable: true,
        headerFilter: { category: 'text', conditionOnly: true },
        minWidth: 200,
        renderCell: (_v, row) => (
          <div className="flex flex-col">
            <span className="text-sm text-text-primary">
              {row.reporterName ?? row.reporterEmail}
            </span>
            <span className="text-xs text-text-tertiary">{row.reporterEmail}</span>
          </div>
        ),
      },
      {
        id: 'tenant',
        accessor: (row) => row.tenantName ?? '',
        header: t('admin.bugReports.columns.tenant'),
        sortable: true,
        headerFilter: { category: 'select', options: tenantOptions },
        minWidth: 170,
        renderCell: (_v, row) => (
          <div className="flex flex-col">
            <span className="text-sm text-text-primary">{row.tenantName}</span>
            <span className="font-mono text-xs text-text-tertiary">{row.tenantCode}</span>
          </div>
        ),
      },
      {
        id: 'version',
        accessor: (row) => row.appVersion ?? '',
        header: t('admin.bugReports.columns.version'),
        sortable: true,
        headerFilter: { category: 'select', options: versionOptions },
        minWidth: 110,
        renderCell: (_v, row) => (
          <span className="font-mono text-xs text-text-secondary">{row.appVersion ?? '—'}</span>
        ),
      },
      {
        id: 'subject',
        accessor: (row) => row.message,
        header: t('admin.bugReports.columns.subject'),
        sortable: true,
        headerFilter: { category: 'text', conditionOnly: true },
        minWidth: 260,
        renderCell: (_v, row) => (
          <div className="flex max-w-sm items-center gap-2">
            <span className="truncate text-sm text-text-primary" title={row.message}>
              {row.message}
            </span>
            {row.screenshots > 0 && (
              <span
                className="flex shrink-0 items-center gap-0.5 text-xs text-text-muted"
                title={t('admin.bugReports.screenshotsCount', { count: row.screenshots })}
              >
                <Paperclip className="h-3.5 w-3.5" aria-hidden="true" />
                {row.screenshots}
              </span>
            )}
          </div>
        ),
      },
      {
        id: 'priority',
        accessor: (row) => row.priority,
        header: t('admin.bugReports.columns.priority'),
        sortable: true,
        headerFilter: { category: 'select', options: priorityOptions },
        minWidth: 150,
        renderCell: (_v, row) => (
          <div className="flex items-center gap-2" onClick={stopRowOpen}>
            <span
              aria-hidden="true"
              className={cn('h-2 w-2 shrink-0 rounded-full', PRIORITY_DOT[row.priority])}
            />
            <SearchableSelect
              value={row.priority}
              onChange={(v) =>
                v && triageMutation.mutate({ id: row.id, patch: { priority: v as BugPriority } })
              }
              options={priorityOptions}
              className="w-full"
            />
          </div>
        ),
      },
      {
        id: 'status',
        accessor: (row) => row.status,
        header: t('admin.bugReports.columns.status'),
        sortable: true,
        headerFilter: { category: 'select', options: statusOptions },
        minWidth: 150,
        renderCell: (_v, row) => (
          <div onClick={stopRowOpen}>
            <SearchableSelect
              value={row.status}
              onChange={(v) =>
                v && triageMutation.mutate({ id: row.id, patch: { status: v as BugStatus } })
              }
              options={statusOptions}
              className="w-full"
            />
          </div>
        ),
      },
      {
        id: 'assignee',
        accessor: (row) => row.assigneeName ?? row.assigneeEmail ?? '',
        header: t('admin.bugReports.columns.assignee'),
        sortable: true,
        headerFilter: { category: 'select', options: assigneeOptions },
        minWidth: 180,
        renderCell: (_v, row) => (
          <div onClick={stopRowOpen}>
            <SearchableSelect
              value={row.assigneeAdminId ?? UNASSIGNED}
              onChange={(v) =>
                triageMutation.mutate({
                  id: row.id,
                  patch: { assigneeAdminId: v === UNASSIGNED || v === null ? null : v },
                })
              }
              options={assigneeOptions}
              className="w-full"
            />
          </div>
        ),
      },
      {
        id: 'caretaker',
        accessor: (row) => row.caretaker,
        header: t('admin.bugReports.columns.caretaker'),
        sortable: true,
        headerFilter: { category: 'select', options: caretakerOptions },
        minWidth: 150,
        renderCell: (_v, row) => (
          <span className="text-sm text-text-secondary">{row.caretaker || '—'}</span>
        ),
      },
    ],
    [
      t,
      tenantOptions,
      versionOptions,
      caretakerOptions,
      priorityOptions,
      statusOptions,
      assigneeOptions,
      triageMutation,
    ],
  );

  return (
    <Layout title={t('admin.bugReports.title')}>
      <section className="px-6 py-8">
        <div className="mb-6 flex items-start gap-3">
          <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-accent-500/10 text-accent-600">
            <Bug className="h-6 w-6" aria-hidden="true" />
          </span>
          <div className="min-w-0 flex-1">
            <h2 className="text-2xl font-semibold text-text-primary">
              {t('admin.bugReports.title')}
            </h2>
            <p className="mt-1 text-sm text-text-tertiary">{t('admin.bugReports.subtitle')}</p>
          </div>
          <div className="flex shrink-0 items-center gap-0.5 rounded-lg bg-bg-tertiary p-0.5">
            <IconButton
              aria-label={t('admin.bugReports.viewTable')}
              title={t('admin.bugReports.viewTable')}
              onClick={() => setView('table')}
              className={cn(view === 'table' && 'bg-bg-elevated text-accent-600 shadow-sm')}
            >
              <List aria-hidden="true" />
            </IconButton>
            <IconButton
              aria-label={t('admin.bugReports.viewGallery')}
              title={t('admin.bugReports.viewGallery')}
              onClick={() => setView('gallery')}
              className={cn(view === 'gallery' && 'bg-bg-elevated text-accent-600 shadow-sm')}
            >
              <LayoutGrid aria-hidden="true" />
            </IconButton>
          </div>
        </div>

        <div className="mb-6 grid grid-cols-2 gap-3 sm:grid-cols-4">
          {[
            { label: t('admin.bugReports.kpi.total'), value: kpis.total },
            { label: t('admin.bugReports.kpi.new'), value: kpis.new },
            { label: t('admin.bugReports.kpi.inProgress'), value: kpis.inProgress },
            { label: t('admin.bugReports.kpi.highPriority'), value: kpis.highPriority },
          ].map((kpi) => (
            <div key={kpi.label} className="rounded-xl bg-bg-tertiary/50 px-4 py-3">
              <p className="text-xs text-text-tertiary">{kpi.label}</p>
              <p className="mt-0.5 text-2xl font-semibold text-text-primary">{kpi.value}</p>
            </div>
          ))}
        </div>

        {reportsQuery.isLoading ? (
          <div className="flex flex-col gap-2">
            {Array.from({ length: 5 }).map((_, i) => (
              <Skeleton key={i} height={48} />
            ))}
          </div>
        ) : reportsQuery.isError ? (
          <div
            role="alert"
            className="rounded-xl border border-red-300 bg-red-50 p-6 text-sm text-red-700 dark:bg-red-900/20 dark:text-red-300"
          >
            {t('admin.bugReports.loadError')}
          </div>
        ) : view === 'table' ? (
          <PreviewTenantAuthBoundary>
            <ConfigurableTable<BugReportRow>
              tableId="admin-bug-reports"
              columns={columns}
              data={rows}
              getRowId={(row) => row.id}
              onRowClick={(row) => setSelectedId(row.id)}
              persistConfig={false}
              showViewModeToggle={false}
              emptyIcon={Bug}
              emptyTitle={t('admin.bugReports.empty')}
              emptyDescription=""
            />
          </PreviewTenantAuthBoundary>
        ) : rows.length === 0 ? (
          <div className="py-10 text-center text-sm text-text-muted">
            {t('admin.bugReports.empty')}
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3">
            {rows.map((row) => (
              <div
                key={row.id}
                role="button"
                tabIndex={0}
                onClick={() => setSelectedId(row.id)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    setSelectedId(row.id);
                  }
                }}
                className="flex cursor-pointer flex-col gap-3 rounded-xl border border-border-secondary bg-bg-elevated p-4 text-left transition-shadow hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-500"
                data-testid={`bug-report-card-${row.id}`}
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="flex items-center gap-2">
                    <Badge tone={PRIORITY_TONE[row.priority]}>{priorityLabel(row.priority)}</Badge>
                    <Badge tone={STATUS_TONE[row.status]}>{statusLabel(row.status)}</Badge>
                  </div>
                  <span className="whitespace-nowrap font-mono text-[11px] text-text-muted">
                    {row.appVersion ?? '—'}
                  </span>
                </div>
                <p className="line-clamp-2 text-sm font-medium text-text-primary">{row.message}</p>
                <div className="mt-auto flex w-full items-end justify-between gap-2">
                  <div className="min-w-0">
                    <p className="truncate text-sm text-text-secondary">{row.tenantName}</p>
                    <p className="truncate text-xs text-text-tertiary">
                      {row.reporterName ?? row.reporterEmail}
                    </p>
                  </div>
                  <div className="flex shrink-0 flex-col items-end gap-1">
                    <span className="font-mono text-[11px] text-text-muted">
                      {formatDateTime(row.createdAt)}
                    </span>
                    {row.screenshots > 0 && (
                      <span className="flex items-center gap-0.5 text-xs text-text-muted">
                        <Paperclip className="h-3.5 w-3.5" aria-hidden="true" />
                        {row.screenshots}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {reportsQuery.data && (
          <p className="mt-3 text-xs text-text-muted">
            {t('admin.bugReports.countLabel', { count: rows.length })}
          </p>
        )}
      </section>

      {/* Modal szczegółów */}
      <Modal
        open={selected !== null}
        onClose={() => setSelectedId(null)}
        title={t('admin.bugReports.detail.title')}
        size="lg"
      >
        {selected && (
          <div className="space-y-5">
            <div className="flex flex-wrap items-center gap-2">
              <Badge tone={PRIORITY_TONE[selected.priority]}>
                {priorityLabel(selected.priority)}
              </Badge>
              <Badge tone={STATUS_TONE[selected.status]}>{statusLabel(selected.status)}</Badge>
            </div>

            <dl className="grid grid-cols-2 gap-x-4 gap-y-3 text-sm">
              <div>
                <dt className="text-xs text-text-tertiary">
                  {t('admin.bugReports.columns.reporter')}
                </dt>
                <dd className="text-text-primary">
                  {selected.reporterName ?? selected.reporterEmail}
                </dd>
                <dd className="text-xs text-text-tertiary">{selected.reporterEmail}</dd>
              </div>
              <div>
                <dt className="text-xs text-text-tertiary">
                  {t('admin.bugReports.columns.tenant')}
                </dt>
                <dd className="text-text-primary">{selected.tenantName}</dd>
                <dd className="font-mono text-xs text-text-tertiary">{selected.tenantCode}</dd>
              </div>
              <div>
                <dt className="text-xs text-text-tertiary">
                  {t('admin.bugReports.columns.version')}
                </dt>
                <dd className="font-mono text-text-primary">{selected.appVersion ?? '—'}</dd>
              </div>
              <div>
                <dt className="text-xs text-text-tertiary">
                  {t('admin.bugReports.columns.createdAt')}
                </dt>
                <dd className="text-text-primary">{formatDateTime(selected.createdAt)}</dd>
              </div>
              <div>
                <dt className="text-xs text-text-tertiary">
                  {t('admin.bugReports.columns.caretaker')}
                </dt>
                <dd className="text-text-primary">{selected.caretaker || '—'}</dd>
              </div>
              {selected.pageUrl && (
                <div className="col-span-2">
                  <dt className="text-xs text-text-tertiary">
                    {t('admin.bugReports.detail.page')}
                  </dt>
                  <dd className="truncate text-xs text-text-secondary" title={selected.pageUrl}>
                    {selected.pageUrl}
                  </dd>
                </div>
              )}
            </dl>

            <div>
              <p className="mb-1 text-xs text-text-tertiary">
                {t('admin.bugReports.columns.subject')}
              </p>
              <p className="whitespace-pre-wrap rounded-lg bg-bg-tertiary/50 p-3 text-sm text-text-primary">
                {selected.message}
              </p>
            </div>

            <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
              <div className="flex flex-col gap-1">
                <span className="text-sm font-medium text-text-secondary">
                  {t('admin.bugReports.columns.priority')}
                </span>
                <SearchableSelect
                  value={selected.priority}
                  onChange={(v) =>
                    v &&
                    triageMutation.mutate({
                      id: selected.id,
                      patch: { priority: v as BugPriority },
                    })
                  }
                  options={priorityOptions}
                />
              </div>
              <div className="flex flex-col gap-1">
                <span className="text-sm font-medium text-text-secondary">
                  {t('admin.bugReports.columns.status')}
                </span>
                <SearchableSelect
                  value={selected.status}
                  onChange={(v) =>
                    v &&
                    triageMutation.mutate({ id: selected.id, patch: { status: v as BugStatus } })
                  }
                  options={statusOptions}
                />
              </div>
              <div className="flex flex-col gap-1">
                <span className="text-sm font-medium text-text-secondary">
                  {t('admin.bugReports.columns.assignee')}
                </span>
                <SearchableSelect
                  value={selected.assigneeAdminId ?? UNASSIGNED}
                  onChange={(v) =>
                    triageMutation.mutate({
                      id: selected.id,
                      patch: { assigneeAdminId: v === UNASSIGNED || v === null ? null : v },
                    })
                  }
                  options={assigneeOptions}
                />
              </div>
            </div>

            <div>
              <p className="mb-2 text-xs text-text-tertiary">
                {t('admin.bugReports.detail.screenshots')}
              </p>
              {detailQuery.isLoading ? (
                <p className="text-sm text-text-muted">…</p>
              ) : (detailQuery.data?.length ?? 0) === 0 ? (
                <p className="text-sm text-text-muted">
                  {t('admin.bugReports.detail.noScreenshots')}
                </p>
              ) : (
                <div className="grid grid-cols-3 gap-2 sm:grid-cols-4">
                  {detailQuery.data?.map((att, i) => (
                    <div
                      key={att.id}
                      role="button"
                      tabIndex={0}
                      onClick={() => setLightboxIndex(i)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          setLightboxIndex(i);
                        }
                      }}
                      className="aspect-video cursor-pointer overflow-hidden rounded-lg border border-border-secondary bg-bg-tertiary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-500"
                      aria-label={att.fileName}
                    >
                      <img
                        src={contentUrl(selected.id, att.id)}
                        alt={att.fileName}
                        title={att.fileName}
                        className="h-full w-full object-cover"
                      />
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </Modal>

      {/* Lightbox */}
      <Modal
        open={selected !== null && lightboxIndex !== null && (detailQuery.data?.length ?? 0) > 0}
        onClose={() => setLightboxIndex(null)}
        title={
          detailQuery.data && lightboxIndex !== null
            ? detailQuery.data[lightboxIndex]?.fileName
            : undefined
        }
        size="xl"
      >
        {selected &&
          lightboxIndex !== null &&
          detailQuery.data &&
          detailQuery.data[lightboxIndex] && (
            <div className="flex flex-col items-center gap-3">
              <img
                src={contentUrl(selected.id, detailQuery.data[lightboxIndex].id)}
                alt={detailQuery.data[lightboxIndex].fileName}
                className="max-h-[70vh] w-auto rounded-lg object-contain"
              />
              {detailQuery.data.length > 1 && (
                <div className="flex items-center gap-4">
                  <IconButton
                    aria-label={t('admin.bugReports.detail.prev')}
                    onClick={() =>
                      setLightboxIndex((prev) =>
                        prev === null
                          ? 0
                          : (prev - 1 + detailQuery.data!.length) % detailQuery.data!.length,
                      )
                    }
                  >
                    <ChevronLeft aria-hidden="true" />
                  </IconButton>
                  <span className="text-sm text-text-secondary">
                    {lightboxIndex + 1} / {detailQuery.data.length}
                  </span>
                  <IconButton
                    aria-label={t('admin.bugReports.detail.next')}
                    onClick={() =>
                      setLightboxIndex((prev) =>
                        prev === null ? 0 : (prev + 1) % detailQuery.data!.length,
                      )
                    }
                  >
                    <ChevronRight aria-hidden="true" />
                  </IconButton>
                </div>
              )}
            </div>
          )}
      </Modal>
    </Layout>
  );
}

export default AdminBugReports;
